import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { API_URL } from '../../lib/api';

function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState(() => {
    // Load saved username from localStorage only if remember me was enabled
    const remember = localStorage.getItem('optiquery_remember') === 'true';
    return remember ? (localStorage.getItem('optiquery_username') || '') : '';
  });
  const [password, setPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('optiquery_remember') === 'true';
  });
  const [demoStatus, setDemoStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/demo/status`)
      .then((r) => (r.ok ? r.json() : { demoMode: false }))
      .then((d) => {
        if (!cancelled) setDemoStatus(d);
      })
      .catch(() => {
        if (!cancelled) setDemoStatus({ demoMode: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Save username to localStorage when it changes (only if remember me is checked)
  useEffect(() => {
    if (rememberMe && username) {
      localStorage.setItem('optiquery_username', username);
    } else if (!rememberMe) {
      localStorage.removeItem('optiquery_username');
    }
  }, [username, rememberMe]);

  // Save remember me preference
  useEffect(() => {
    localStorage.setItem('optiquery_remember', rememberMe.toString());
  }, [rememberMe]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    console.log('Forgot password form submitted');
    
    if (!forgotPasswordEmail) {
      setForgotPasswordMessage('Please enter an email address');
      return;
    }
    
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      console.log('Sending forgot password request for:', forgotPasswordEmail);
      
      const res = await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      console.log('Response status:', res.status);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log('Response data:', data);
      
      if (data.success) {
        setForgotPasswordMessage(data.message);
        setForgotPasswordEmail('');
      } else {
        setForgotPasswordMessage(data.message || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordMessage(`Network error: ${error.message}. Please try again.`);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }
    if (mfaRequired && !mfaCode) {
      setError('Please enter your authenticator code.');
      return;
    }
    setLoading(true);
    try {
      console.log('[LOGIN] API URL:', API_URL);
      const body = { username, password };
      if (mfaRequired && mfaCode) body.mfaCode = mfaCode;
      
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed.');
      } else if (data.mfaRequired) {
        setMfaRequired(true);
        setMfaCode('');
        setError('');
      } else {
        setSuccess(true);
        if (data.sessionToken) {
          localStorage.setItem('sessionToken', data.sessionToken);
        }
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      }
    } catch (err) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-brand">
        <img src="/logo.png" alt="OptiPrime Logo" className="login-logo" />
          <span className="brand-text">
            <span className="opti">Opti</span>
            <span className="prime">Prime</span>
          </span>
        </div>
        <h2>Master Admin Login</h2>
        {demoStatus?.demoMode && (
          <div
            style={{
              marginBottom: '14px',
              padding: '10px 12px',
              borderRadius: '8px',
              background: 'rgba(25, 239, 255, 0.12)',
              border: '1px solid rgba(25, 239, 255, 0.45)',
              color: 'var(--text-primary, #e2e8f0)',
              fontSize: '0.88rem',
              lineHeight: 1.45,
            }}
          >
            <strong>Investor demo</strong> — use username <code style={{ color: '#19EFFF' }}>demo</code> and password{' '}
            <code style={{ color: '#19EFFF' }}>demo</code> unless your host set <code>DEMO_LOGIN_USERNAME</code> /{' '}
            <code>DEMO_LOGIN_PASSWORD</code>. MFA is skipped for this session.
          </div>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <div>
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            required
            disabled={loading}
              placeholder="Enter your username"
          />
          </div>
          <div>
          <label htmlFor="password">Password</label>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <input
              id="password"
              type={showLoginPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={loading}
              placeholder="Enter your password"
              style={{ paddingRight: '40px', width: '100%' }}
            />
            <button
              type="button"
              onClick={() => setShowLoginPassword(!showLoginPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#666',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px'
              }}
              title={showLoginPassword ? "Hide password" : "Show password"}
            >
              {showLoginPassword ? '🙈' : '👁️'}
            </button>
          </div>
          </div>
          {mfaRequired && (
            <div>
              <label htmlFor="mfaCode" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span role="img" aria-label="lock">🔐</span> Authenticator Code
              </label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\s/g, ''))}
                placeholder="Enter 6-digit code or recovery code"
                required
                autoFocus
                disabled={loading}
                style={{ letterSpacing: '4px', textAlign: 'center', fontSize: '18px' }}
              />
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                Enter the code from your authenticator app
              </div>
            </div>
          )}
          <div className="remember-me-container">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Remember my username</span>
            </label>
          </div>
          {error && <div className="login-error">{error}</div>}
          {success && <div className="login-success">Login successful!</div>}
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Verifying...' : mfaRequired ? 'Verify & Login' : 'Login'}
          </button>
          {mfaRequired && (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button type="button" onClick={() => { setMfaRequired(false); setMfaCode(''); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#888', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}>
                Back to login
              </button>
            </div>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                console.log('Forgot password link clicked');
                setShowForgotPassword(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#19efff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Forgot Password?
            </button>
          </div>
        </form>
      </div>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--sidebar-bg)',
            padding: '30px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            border: '1px solid var(--sidebar-border)'
          }}>
            <h3 style={{ 
              color: 'var(--text)', 
              marginBottom: '20px', 
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              🔑 Reset Admin Password
            </h3>
            
            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  color: 'var(--text)', 
                  display: 'block', 
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  Admin Email Address
                </label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="contact@optiprime.ai"
                  required
                  disabled={forgotPasswordLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid var(--sidebar-border)',
                    backgroundColor: 'var(--header-bg)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              {forgotPasswordMessage && (
                <div style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  backgroundColor: forgotPasswordMessage.includes('sent') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${forgotPasswordMessage.includes('sent') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  color: forgotPasswordMessage.includes('sent') ? '#10B981' : '#EF4444',
                  fontSize: '14px'
                }}>
                  {forgotPasswordMessage}
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end' 
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  disabled={forgotPasswordLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: '1px solid var(--sidebar-border)',
                    backgroundColor: 'transparent',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading || !forgotPasswordEmail}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#19efff',
                    color: 'white',
                    cursor: forgotPasswordLoading || !forgotPasswordEmail ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: forgotPasswordLoading || !forgotPasswordEmail ? 0.6 : 1
                  }}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
